import { defineMutator } from '@rocicorp/zero';
import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import * as z from 'zod';
import { zql } from '../schema.js';
import { itemsDetail, statusItems } from '../util/event-log-items.js';
import { titleCase } from '../util/index.js';
import { Status } from '../util/schemas.js';
import { assertCan } from '../utils.js';
import { DeleteResourceSchema, EventLogResources, UpdateResourceStatusSchema, addEventLog } from './util.js';
import { upsertCompoundMapping } from './compounders/utils.js';
import { CompoundMappingType } from '../database/schema.js';
export const BasicDataItemSchema = z.object({
    code: z.string().trim().optional(),
    name: z.string().trim().nonempty(),
    description: z.string().optional(),
    status: Status.optional(),
});
export const AddBasicDataItemSchema = BasicDataItemSchema;
export const UpdateBasicDataItemSchema = BasicDataItemSchema.partial().extend({
    id: z.uuidv7().nonempty(),
});
export const AddApplicationFormSchema = AddBasicDataItemSchema.extend({
    preparationRequired: z.boolean().default(false),
    carrierRequired: z.boolean().default(false),
    /**
     * Smart compounder machine master-data id. Keeps a `compound_mappings` row
     * (`sourceId` = this application form's id) in sync for the PATIENT_CONTAINER
     * mapping type. Empty clears the mapping.
     */
    machineId: z.string().trim().optional(),
});
export const UpdateApplicationFormSchema = AddApplicationFormSchema.partial().extend({
    id: z.uuidv7().nonempty(),
});
export const AddMedicineFormSchema = AddBasicDataItemSchema.extend({
    reconstitutionRequired: z.boolean().default(false),
    isDrySubstance: z.boolean().default(false),
});
export const UpdateMedicineFormSchema = AddMedicineFormSchema.partial().extend({
    id: z.uuidv7().nonempty(),
});
export const AddInsuranceCompanySchema = AddBasicDataItemSchema.extend({
    nationalId: z.string().optional(),
});
export const UpdateInsuranceCompanySchema = AddInsuranceCompanySchema.partial().extend({
    id: z.uuidv7().nonempty(),
});
export const AddGeneralPhysicianSchema = AddBasicDataItemSchema.extend({
    specialty: z.string().optional(),
    phone: z.string().optional(),
    email: z.email().optional(),
});
export const UpdateGeneralPhysicianSchema = AddGeneralPhysicianSchema.partial().extend({
    id: z.uuidv7().nonempty(),
});
async function findById(tx, resource, id) {
    return await tx.run(resource.where('id', '=', id).where('deletedAt', 'IS', null).one());
}
// Empty codes are stored as null so rows can be saved without a code
// (the CMS auto-generates them from the name); '' would otherwise collide
// on the unique constraint.
function insertCode(code) {
    return code || null;
}
function updateCode(code) {
    return code == null ? undefined : code || null;
}
function createBasicDataMutators(opts) {
    const { resource, eventLogResource } = opts;
    return {
        add: defineMutator(AddBasicDataItemSchema, async ({ args, ctx, tx }) => {
            assertCan(ctx, 'basic_data.manage');
            const now = Date.now();
            const userActions = {
                createdAt: now,
                createdBy: ctx.user.id,
            };
            const newItem = {
                id: uuidv7(),
                code: insertCode(args.code),
                name: titleCase(args.name),
                description: args.description,
                status: args.status,
                ...userActions,
            };
            await opts.mutate(tx).insert(newItem);
            if (tx.location === 'server') {
                const patches = createPatch({}, newItem);
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: eventLogResource,
                    resourceId: newItem.id,
                    action: 'create',
                    status: 'OK',
                    domain: 'configuration',
                    details: {
                        name: newItem.name,
                        id: newItem.id,
                    },
                    patches,
                    performedBy: ctx.user.id,
                    performedAt: now,
                }, ctx);
            }
        }),
        update: defineMutator(UpdateBasicDataItemSchema, async ({ args, ctx, tx }) => {
            assertCan(ctx, 'basic_data.manage');
            const prev = await findById(tx, resource, args.id);
            if (!prev)
                throw new Error('Not Found');
            const now = Date.now();
            const userActions = {
                updatedAt: now,
                updatedBy: ctx.user.id,
            };
            const updatedItem = {
                id: args.id,
                code: updateCode(args.code),
                name: args.name != null ? titleCase(args.name) : undefined,
                description: args.description,
                status: args.status,
                ...userActions,
            };
            await opts.mutate(tx).update(updatedItem);
            if (tx.location === 'server') {
                const patches = createPatch(prev, updatedItem);
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: eventLogResource,
                    resourceId: args.id,
                    action: 'update',
                    status: 'OK',
                    domain: 'configuration',
                    details: {
                        name: updatedItem.name,
                        id: updatedItem.id,
                    },
                    patches,
                    performedBy: ctx.user.id,
                    performedAt: now,
                }, ctx);
            }
        }),
        updateStatus: defineMutator(UpdateResourceStatusSchema, async ({ args, ctx, tx }) => {
            assertCan(ctx, 'basic_data.manage');
            const prev = await findById(tx, resource, args.id);
            if (!prev)
                throw new Error('Not found');
            const now = Date.now();
            const updatedItem = {
                id: args.id,
                status: args.status,
                updatedAt: now,
                updatedBy: ctx.user.id,
            };
            await opts.mutate(tx).update(updatedItem);
            if (tx.location === 'server') {
                const patches = createPatch(prev, updatedItem);
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: eventLogResource,
                    resourceId: args.id,
                    action: 'update',
                    patches,
                    status: args.status === 'inactive' ? 'WARNING' : 'INFO',
                    domain: 'configuration',
                    details: {
                        name: prev.name,
                        id: updatedItem.id,
                        ...itemsDetail(statusItems(prev.status, args.status)),
                    },
                    performedBy: updatedItem.updatedBy,
                    performedAt: updatedItem.updatedAt,
                }, ctx);
            }
        }),
        delete: defineMutator(DeleteResourceSchema, async ({ args, ctx, tx }) => {
            assertCan(ctx, 'basic_data.manage');
            const prev = await findById(tx, resource, args.id);
            if (!prev)
                throw new Error('Not found');
            const now = Date.now();
            const updatedItem = {
                id: args.id,
                // keep uniqueness on `code` after soft-delete (null codes stay null)
                code: prev.code != null ? `${prev.code}_deleted_${now}` : undefined,
                name: `${prev.name}_deleted_${now}`,
                deletedAt: now,
                deletedBy: ctx.user.id,
            };
            await opts.mutate(tx).update(updatedItem);
            if (tx.location === 'server') {
                const patches = createPatch(prev, updatedItem);
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: eventLogResource,
                    resourceId: args.id,
                    action: 'delete',
                    patches,
                    status: 'WARNING',
                    domain: 'configuration',
                    details: {
                        name: prev.name,
                        id: updatedItem.id,
                    },
                    performedBy: updatedItem.deletedBy,
                    performedAt: updatedItem.deletedAt,
                }, ctx);
            }
        }),
    };
}
export const basicData = {
    adminRoutes: createBasicDataMutators({
        resource: zql.adminRoutes,
        eventLogResource: EventLogResources.administrationRoute,
        mutate: tx => tx.mutate.adminRoutes,
    }),
    applicationForms: {
        ...createBasicDataMutators({
            resource: zql.applicationForms,
            eventLogResource: EventLogResources.applicationForm,
            mutate: tx => tx.mutate.applicationForms,
        }),
        add: defineMutator(AddApplicationFormSchema, async ({ args, ctx, tx }) => {
            assertCan(ctx, 'basic_data.manage');
            const now = Date.now();
            const userActions = {
                createdAt: now,
                createdBy: ctx.user.id,
            };
            const newItem = {
                id: uuidv7(),
                code: insertCode(args.code),
                name: titleCase(args.name),
                description: args.description,
                status: args.status,
                preparationRequired: Boolean(args.preparationRequired),
                carrierRequired: Boolean(args.carrierRequired),
                ...userActions,
            };
            await tx.mutate.applicationForms.insert(newItem);
            await upsertCompoundMapping(tx, {
                mappingType: CompoundMappingType.enum.PATIENT_CONTAINER,
                sourceId: newItem.id,
                machineId: args.machineId,
                actorId: ctx.user.id,
                now,
            });
            if (tx.location === 'server') {
                const patches = createPatch({}, newItem);
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.applicationForm,
                    resourceId: newItem.id,
                    action: 'create',
                    patches,
                    details: {
                        name: newItem.name,
                        id: newItem.id,
                    },
                    performedBy: ctx.user.id,
                    performedAt: now,
                }, ctx);
            }
        }),
        update: defineMutator(UpdateApplicationFormSchema, async ({ args, ctx, tx }) => {
            assertCan(ctx, 'basic_data.manage');
            const prev = await findById(tx, zql.applicationForms, args.id);
            if (!prev)
                throw new Error('Not Found');
            const now = Date.now();
            const userActions = {
                updatedAt: now,
                updatedBy: ctx.user.id,
            };
            const updatedItem = {
                id: args.id,
                code: updateCode(args.code),
                name: args.name != null ? titleCase(args.name) : undefined,
                description: args.description,
                status: args.status,
                preparationRequired: Boolean(args.preparationRequired),
                carrierRequired: Boolean(args.carrierRequired),
                ...userActions,
            };
            await tx.mutate.applicationForms.update(updatedItem);
            await upsertCompoundMapping(tx, {
                mappingType: CompoundMappingType.enum.PATIENT_CONTAINER,
                sourceId: args.id,
                machineId: args.machineId,
                actorId: ctx.user.id,
                now,
            });
            if (tx.location === 'server') {
                const patches = createPatch(prev, updatedItem);
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.applicationForm,
                    resourceId: args.id,
                    action: 'update',
                    patches,
                    details: {
                        name: updatedItem.name,
                        id: updatedItem.id,
                    },
                    performedBy: ctx.user.id,
                    performedAt: now,
                }, ctx);
            }
        }),
    },
    containerTypes: createBasicDataMutators({
        resource: zql.containerTypes,
        eventLogResource: EventLogResources.containerType,
        mutate: tx => tx.mutate.containerTypes,
    }),
    containerMaterials: createBasicDataMutators({
        resource: zql.containerMaterials,
        eventLogResource: EventLogResources.containerMaterial,
        mutate: tx => tx.mutate.containerMaterials,
    }),
    diagnoses: createBasicDataMutators({
        resource: zql.diagnoses,
        eventLogResource: EventLogResources.diagnosis,
        mutate: tx => tx.mutate.diagnoses,
    }),
    hazardClasses: createBasicDataMutators({
        resource: zql.hazardClasses,
        eventLogResource: EventLogResources.hazardClass,
        mutate: tx => tx.mutate.hazardClasses,
    }),
    cancerTypes: createBasicDataMutators({
        resource: zql.cancerTypes,
        eventLogResource: EventLogResources.cancerType,
        mutate: tx => tx.mutate.cancerTypes,
    }),
    companies: createBasicDataMutators({
        resource: zql.companies,
        eventLogResource: EventLogResources.company,
        mutate: tx => tx.mutate.companies,
    }),
    stockLocations: createBasicDataMutators({
        resource: zql.stockLocations,
        eventLogResource: EventLogResources.stockLocation,
        mutate: tx => tx.mutate.stockLocations,
    }),
    treatmentChairs: createBasicDataMutators({
        resource: zql.treatmentChairs,
        eventLogResource: EventLogResources.treatmentChair,
        mutate: tx => tx.mutate.treatmentChairs,
    }),
    storageLabels: createBasicDataMutators({
        resource: zql.storageLabels,
        eventLogResource: EventLogResources.storageLabel,
        mutate: tx => tx.mutate.storageLabels,
    }),
    insuranceCompanies: {
        ...createBasicDataMutators({
            resource: zql.insuranceCompanies,
            eventLogResource: EventLogResources.insuranceCompany,
            mutate: tx => tx.mutate.insuranceCompanies,
        }),
        add: defineMutator(AddInsuranceCompanySchema, async ({ args, ctx, tx }) => {
            assertCan(ctx, 'basic_data.manage');
            const now = Date.now();
            const userActions = {
                createdAt: now,
                createdBy: ctx.user.id,
            };
            const newItem = {
                id: uuidv7(),
                code: insertCode(args.code),
                name: titleCase(args.name),
                description: args.description,
                nationalId: args.nationalId,
                status: args.status,
                ...userActions,
            };
            await tx.mutate.insuranceCompanies.insert(newItem);
            if (tx.location === 'server') {
                const patches = createPatch({}, newItem);
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.insuranceCompany,
                    resourceId: newItem.id,
                    action: 'create',
                    patches,
                    details: {
                        name: newItem.name,
                        id: newItem.id,
                    },
                    performedBy: ctx.user.id,
                    performedAt: now,
                }, ctx);
            }
        }),
        update: defineMutator(UpdateInsuranceCompanySchema, async ({ args, ctx, tx }) => {
            assertCan(ctx, 'basic_data.manage');
            const prev = await findById(tx, zql.insuranceCompanies, args.id);
            if (!prev)
                throw new Error('Not Found');
            const now = Date.now();
            const userActions = {
                updatedAt: now,
                updatedBy: ctx.user.id,
            };
            const updatedItem = {
                id: args.id,
                code: updateCode(args.code),
                name: args.name != null ? titleCase(args.name) : undefined,
                description: args.description,
                nationalId: args.nationalId,
                status: args.status,
                ...userActions,
            };
            await tx.mutate.insuranceCompanies.update(updatedItem);
            if (tx.location === 'server') {
                const patches = createPatch(prev, updatedItem);
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.insuranceCompany,
                    resourceId: args.id,
                    action: 'update',
                    patches,
                    details: {
                        name: updatedItem.name,
                        id: updatedItem.id,
                    },
                    performedBy: ctx.user.id,
                    performedAt: now,
                }, ctx);
            }
        }),
    },
    medicineForms: {
        ...createBasicDataMutators({
            resource: zql.medicineForms,
            eventLogResource: EventLogResources.medicineForm,
            mutate: tx => tx.mutate.medicineForms,
        }),
        add: defineMutator(AddMedicineFormSchema, async ({ args, ctx, tx }) => {
            assertCan(ctx, 'basic_data.manage');
            const now = Date.now();
            const userActions = {
                createdAt: now,
                createdBy: ctx.user.id,
            };
            const newItem = {
                id: uuidv7(),
                code: insertCode(args.code),
                name: args.name,
                description: args.description,
                status: args.status,
                reconstitutionRequired: Boolean(args.reconstitutionRequired),
                isDrySubstance: Boolean(args.isDrySubstance),
                ...userActions,
            };
            await tx.mutate.medicineForms.insert(newItem);
            if (tx.location === 'server') {
                const patches = createPatch({}, newItem);
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.medicineForm,
                    resourceId: newItem.id,
                    action: 'create',
                    patches,
                    details: {
                        name: newItem.name,
                        id: newItem.id,
                    },
                    performedBy: ctx.user.id,
                    performedAt: now,
                }, ctx);
            }
        }),
        update: defineMutator(UpdateMedicineFormSchema, async ({ args, ctx, tx }) => {
            assertCan(ctx, 'basic_data.manage');
            const prev = await findById(tx, zql.medicineForms, args.id);
            if (!prev)
                throw new Error('Not Found');
            const now = Date.now();
            const userActions = {
                updatedAt: now,
                updatedBy: ctx.user.id,
            };
            const updatedItem = {
                id: args.id,
                code: updateCode(args.code),
                name: args.name,
                description: args.description,
                status: args.status,
                reconstitutionRequired: Boolean(args.reconstitutionRequired),
                isDrySubstance: Boolean(args.isDrySubstance),
                ...userActions,
            };
            await tx.mutate.medicineForms.update(updatedItem);
            if (tx.location === 'server') {
                const patches = createPatch(prev, updatedItem);
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.medicineForm,
                    resourceId: args.id,
                    action: 'update',
                    patches,
                    details: {
                        name: updatedItem.name,
                        id: updatedItem.id,
                    },
                    performedBy: ctx.user.id,
                    performedAt: now,
                }, ctx);
            }
        }),
    },
    generalPhysicians: {
        ...createBasicDataMutators({
            resource: zql.generalPhysicians,
            eventLogResource: EventLogResources.generalPhysician,
            mutate: tx => tx.mutate.generalPhysicians,
        }),
        add: defineMutator(AddGeneralPhysicianSchema, async ({ args, ctx, tx }) => {
            assertCan(ctx, 'basic_data.manage');
            const now = Date.now();
            const userActions = {
                createdAt: now,
                createdBy: ctx.user.id,
            };
            const newItem = {
                id: uuidv7(),
                code: insertCode(args.code),
                name: args.name,
                specialty: args.specialty,
                phone: args.phone,
                email: args.email,
                description: args.description,
                status: args.status,
                ...userActions,
            };
            await tx.mutate.generalPhysicians.insert(newItem);
            if (tx.location === 'server') {
                const patches = createPatch({}, newItem);
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.generalPhysician,
                    resourceId: newItem.id,
                    action: 'create',
                    patches,
                    details: {
                        name: newItem.name,
                        id: newItem.id,
                    },
                    performedBy: ctx.user.id,
                    performedAt: now,
                }, ctx);
            }
        }),
        update: defineMutator(UpdateGeneralPhysicianSchema, async ({ args, ctx, tx }) => {
            assertCan(ctx, 'basic_data.manage');
            const prev = await findById(tx, zql.generalPhysicians, args.id);
            if (!prev)
                throw new Error('Not Found');
            const now = Date.now();
            const userActions = {
                updatedAt: now,
                updatedBy: ctx.user.id,
            };
            const updatedItem = {
                id: args.id,
                code: updateCode(args.code),
                name: args.name,
                specialty: args.specialty,
                phone: args.phone,
                email: args.email,
                description: args.description,
                status: args.status,
                ...userActions,
            };
            await tx.mutate.generalPhysicians.update(updatedItem);
            if (tx.location === 'server') {
                const patches = createPatch(prev, updatedItem);
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.generalPhysician,
                    resourceId: args.id,
                    action: 'update',
                    patches,
                    details: {
                        name: updatedItem.name,
                        id: updatedItem.id,
                    },
                    performedBy: ctx.user.id,
                    performedAt: now,
                }, ctx);
            }
        }),
    },
};
//# sourceMappingURL=basic-data.mutators.js.map